import {Area, Spot, Zone} from "../types";

export const WAV_WARNING_REASONS: string[] = [
    "Higher data usage — WAV files are uncompressed and several times larger than MP3 or AAC.",
    "Higher latency, especially with group sync — large files take longer to buffer before playback.",
    "Less resilience — buffers run out faster on slow connections, increasing the risk of audible underruns.",
];

const isWavName = (fileName?: string): boolean => {
    if (!fileName) {
        return false;
    }
    return /\.wav$/i.test(fileName.trim());
};

export const isWavSoundFile = (soundFile: any): boolean => {
    if (!soundFile) {
        return false;
    }
    return isWavName(soundFile.fileName) || isWavName(soundFile.originalFileName);
};

export const isWavLibraryEntry = (entry: any): boolean => {
    if (!entry) {
        return false;
    }
    return isWavName(entry.originalFileName) || isWavName(entry.objectId);
};

export const spotHasWavSoundFile = (spot: Spot): boolean => {
    if (!spot?.soundFiles) {
        return false;
    }
    return spot.soundFiles.some(isWavSoundFile);
};

export const zoneHasWavSoundFile = (zone: Zone): boolean => {
    if (!zone?.soundFiles) {
        return false;
    }
    return Object.values(zone.soundFiles).some((soundFile) => isWavSoundFile(soundFile));
};

const collectAreaZones = (area: Area): Array<{label: string; zone: Zone}> => {
    const zones = area.zones;
    if (!zones) {
        return [];
    }
    const out: Array<{label: string; zone: Zone}> = [];
    if (zones.boundaries) out.push({label: zones.boundaries.label || zones.boundaries.name || "Boundaries", zone: zones.boundaries});
    if (zones.entry) out.push({label: zones.entry.label || zones.entry.name || "Entry", zone: zones.entry});
    if (zones.exit) out.push({label: zones.exit.label || zones.exit.name || "Exit", zone: zones.exit});
    if (zones.maintenance) out.push({label: zones.maintenance.label || zones.maintenance.name || "Maintenance", zone: zones.maintenance});
    if (Array.isArray(zones.ambientZones)) {
        zones.ambientZones.forEach((ambient) => {
            out.push({label: ambient.label || ambient.name || "Ambient zone", zone: ambient});
        });
    }
    return out;
};

export type WavCarrier = {kind: "spot" | "zone"; label: string};

export const findWavCarriers = (area: Area, spots: Spot[]): WavCarrier[] => {
    const carriers: WavCarrier[] = [];
    spots.forEach((spot) => {
        if (spotHasWavSoundFile(spot)) {
            carriers.push({kind: "spot", label: spot.name || spot.id});
        }
    });
    collectAreaZones(area).forEach(({label, zone}) => {
        if (zoneHasWavSoundFile(zone)) {
            carriers.push({kind: "zone", label: label});
        }
    });
    return carriers;
};
